import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { RotateCw } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { logout } from "../../features/userSlice";
import { useContextStateManagement } from "../../context/ContextApi";
import CallsBreakdownDialog from "../../child-components/CallDialogs/CallsBreakdownDialog";
import DateRangePicker from "../Home/DateRangePicker";
import { DEFAULT_PRACTICE_TIMEZONE, practiceDateKey, formatInPracticeTimezone } from "../../practiceTimezone";
import InfoTooltip from "../common/InfoTooltip";
import "./Dashboard.css";

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

// localDateKey: "YYYY-MM-DD" from the browser's own local date parts, instead of
// toISOString() (which reports the UTC date - a call placed in the evening can roll over to
// "tomorrow" hours before local midnight, or "today"/"first of month" defaults can land on the
// wrong day depending on time-of-day and the viewer's UTC offset).
const localDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Trend chart default: a 7-day window instead of month-to-date. A wide default range stretched
// each day's (often sparse) call count across many points, so the trend line spent most of its
// length hugging zero - a shorter default keeps each visible day's variation legible.
const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return localDateKey(date);
};

const formatCountValue = (value) => Number(value || 0).toLocaleString("en-US");

const formatMinutesValue = (mins) => {
  const total = Math.max(0, mins | 0);
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const sumLegMinutes = (conversations) =>
  (conversations || []).reduce((sum, conversation) => {
    const legs = Array.isArray(conversation?.legs) ? conversation.legs : [];
    if (legs.length === 0) {
      const seconds = Number(conversation?.durationSec) || 0;
      return sum + Math.max(0, Math.ceil(seconds / 60));
    }

    return (
      sum +
      legs.reduce(
        (legSum, leg) =>
          legSum + Math.max(0, Math.ceil((Number(leg?.durationSec) || 0) / 60)),
        0,
      )
    );
  }, 0);

// Raw, unrounded root/inbound leg duration, summed once at the end - as opposed to
// sumLegMinutes's per-leg-rounded-up Twilio-billed minutes, which counts every leg
// (root/inbound + AI + transfer) since that's how Twilio actually bills each hop. Matches
// CallsBreakdownDialog's own "Talk time" card definition (the root leg's own duration, the whole
// call end-to-end) so a KPI card showing "Actual" and the dialog it opens into always agree.
const sumTalkTimeSeconds = (conversations) =>
  (conversations || []).reduce((sum, conversation) => {
    const legs = Array.isArray(conversation?.legs) ? conversation.legs : [];
    if (legs.length === 0) {
      return sum + (Number(conversation?.durationSec) || 0);
    }
    return (
      sum +
      legs
        .filter((leg) => leg?.legLevel === "root" || leg?.role === "root")
        .reduce((legSum, leg) => legSum + (Number(leg?.durationSec) || 0), 0)
    );
  }, 0);

const filterConversationsByDirection = (conversationsList, directionFilter) => {
  const list = Array.isArray(conversationsList) ? conversationsList : [];

  if (directionFilter === "inbound") {
    return list.filter((conversation) => conversation?.direction === "inbound");
  }

  if (directionFilter === "outbound") {
    return list.filter((conversation) => conversation?.direction === "outbound");
  }

  return list;
};

const formatTrendLabel = (date, timeZone = DEFAULT_PRACTICE_TIMEZONE) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);

// Compact "Jul 5-11" / "Jul 28-Aug 3" label for a weekly bucket, instead of concatenating two
// full "Sat, Jul 5" labels into "Sat, Jul 5 - Sat, Jul 12" (~24 characters) - that full form is
// almost twice as wide as the chart's per-label budget accounts for, which is what caused
// consecutive weekly labels to overlap into an unreadable block once a range spans 60 days.
const formatWeekRangeLabel = (startDate, endDate, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const monthFormatter = new Intl.DateTimeFormat("en-US", { timeZone, month: "short" });
  const dayFormatter = new Intl.DateTimeFormat("en-US", { timeZone, day: "numeric" });
  const startMonth = monthFormatter.format(startDate);
  const endMonth = monthFormatter.format(endDate);

  if (startMonth === endMonth) {
    return `${startMonth} ${dayFormatter.format(startDate)}-${dayFormatter.format(endDate)}`;
  }

  return `${startMonth} ${dayFormatter.format(startDate)}-${endMonth} ${dayFormatter.format(endDate)}`;
};

// stepDateKey: add/subtract whole calendar days from a "YYYY-MM-DD" key via UTC-noon
// arithmetic, so it never depends on the browser's own local timezone (a plain JS Date's
// setDate() would drift by a day near a DST boundary or when the browser's zone differs from
// the practice's).
const stepDateKey = (dateKey, days) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
};

// Shared thresholds so "good/needs attention/problem" reads the same color everywhere on the
// page (Completion Rate, Transfers success, etc.) instead of each card picking its own cutoffs.
const toneForRate = (rate) => (rate >= 80 ? "good" : rate >= 50 ? "warning" : "danger");

const formatHourLabel = (hour) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
  }).format(new Date(2026, 0, 1, hour));

const buildBusiestHours = (conversationsList, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const scoped = Array.isArray(conversationsList) ? conversationsList : [];
  const buckets = new Map();
  let validCalls = 0;

  scoped.forEach((conversation) => {
    if (!conversation?.startedAt) return;
    const startedAt = new Date(conversation.startedAt);
    if (Number.isNaN(startedAt.getTime())) return;
    validCalls += 1;
    // Bucketed in the resolved (practice/user) timezone, not the viewer's browser-local hour -
    // every other time-bucketed chart on this page already does this (buildCompletedTrend,
    // today's transferSummary check); this one was the odd one out.
    const hour = Number(formatInPracticeTimezone(startedAt, timeZone).slice(11, 13));
    buckets.set(hour, (buckets.get(hour) || 0) + 1);
  });

  const totalCalls = validCalls || 1;

  return Array.from(buckets.entries())
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0] - right[0];
    })
    .slice(0, 4)
    .map(([hour, count]) => ({
      key: hour,
      label: formatHourLabel(hour),
      count,
      share: Math.round((count / totalCalls) * 100),
    }));
};

// Trailing-window cap for pathologically wide date-range picks (e.g. a
// full year) so the chart doesn't try to plot/label an unbounded number of
// points; typical single/multi-month ranges are well under this.
const MAX_TREND_DAYS = 60;

// Beyond ~2 weeks, a daily grid over normally-sparse call volume stretches into a long mostly-flat
// line - group into trailing 7-day buckets instead so real variation is still visible as a shape.
const TREND_WEEKLY_BUCKET_THRESHOLD_DAYS = 14;

const buildCompletedTrend = (
  conversationsList,
  startDate,
  endDate,
  timeZone = DEFAULT_PRACTICE_TIMEZONE,
) => {
  const scopedConversations = Array.isArray(conversationsList) ? conversationsList : [];
  const todayKey = practiceDateKey(new Date(), timeZone);
  const endKey = endDate || startDate || todayKey;
  const startKey = startDate || endDate || todayKey;
  const normalizedStartKey = startKey <= endKey ? startKey : endKey;
  const normalizedEndKey = endKey >= startKey ? endKey : startKey;

  const spanDays =
    Math.round(
      (new Date(`${normalizedEndKey}T12:00:00Z`) - new Date(`${normalizedStartKey}T12:00:00Z`)) /
        (24 * 60 * 60 * 1000),
    ) + 1;
  const dayCount = Math.min(Math.max(spanDays, 1), MAX_TREND_DAYS);

  const dayKeys = Array.from({ length: dayCount }, (_, index) =>
    stepDateKey(normalizedEndKey, -(dayCount - 1 - index)),
  );

  const countsByDay = new Map(dayKeys.map((key) => [key, { inbound: 0, outbound: 0, total: 0 }]));

  scopedConversations.forEach((conversation) => {
    const dayKey = practiceDateKey(conversation?.startedAt, timeZone);
    if (!dayKey || !countsByDay.has(dayKey)) return;

    const currentDay = countsByDay.get(dayKey);
    // Every attempt counts toward "total" regardless of outcome, so the chart still has visible
    // shape on days with few/no completions - only the Inbound/Outbound Completed lines below
    // are restricted to conversation.status === "completed".
    currentDay.total += 1;

    if (conversation?.status !== "completed") return;
    if (conversation?.direction === "outbound") {
      currentDay.outbound += 1;
    } else {
      currentDay.inbound += 1;
    }
  });

  const dailyPoints = dayKeys.map((key) => {
    const counts = countsByDay.get(key) || { inbound: 0, outbound: 0, total: 0 };

    return {
      key,
      label: formatTrendLabel(new Date(`${key}T12:00:00Z`), timeZone),
      inbound: counts.inbound,
      outbound: counts.outbound,
      total: counts.total,
    };
  });

  if (dailyPoints.length <= TREND_WEEKLY_BUCKET_THRESHOLD_DAYS) {
    return dailyPoints;
  }

  const weeklyPoints = [];
  for (let index = 0; index < dailyPoints.length; index += 7) {
    const chunk = dailyPoints.slice(index, index + 7);
    const inbound = chunk.reduce((sum, point) => sum + point.inbound, 0);
    const outbound = chunk.reduce((sum, point) => sum + point.outbound, 0);
    const total = chunk.reduce((sum, point) => sum + point.total, 0);
    const label =
      chunk.length > 1
        ? formatWeekRangeLabel(
            new Date(`${chunk[0].key}T12:00:00Z`),
            new Date(`${chunk[chunk.length - 1].key}T12:00:00Z`),
            timeZone,
          )
        : chunk[0].label;

    weeklyPoints.push({ key: chunk[0].key, label, inbound, outbound, total });
  }

  return weeklyPoints;
};

// Minimum horizontal room per bar (SVG px) - keeps every bar and its label at a readable width
// no matter how many periods are in range, by growing the chart wider and letting it scroll
// instead of squeezing bars/labels together the way the old fixed-width line chart did.
const CHART_MIN_WIDTH = 560;
const CHART_SLOT_WIDTH = 76;
const CHART_HEIGHT = 300;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="dash-chart-tooltip">
      <strong>{label}</strong>
      <div className="dash-chart-tooltip-row">
        <span className="dash-chart-tooltip-dot dash-chart-tooltip-dot--total" />
        <span>Total Calls</span>
        <em>{point.total}</em>
      </div>
      {point.inbound > 0 ? (
        <div className="dash-chart-tooltip-row">
          <span className="dash-chart-tooltip-dot dash-chart-tooltip-dot--inbound" />
          <span>Inbound Completed</span>
          <em>{point.inbound}</em>
        </div>
      ) : null}
      {point.outbound > 0 ? (
        <div className="dash-chart-tooltip-row">
          <span className="dash-chart-tooltip-dot dash-chart-tooltip-dot--outbound" />
          <span>Outbound Completed</span>
          <em>{point.outbound}</em>
        </div>
      ) : null}
    </div>
  );
};

// Stacked bar per day/week: the colored segments are completed calls (split by direction), and
// the gray segment on top is the rest of that period's total - so the full bar height always
// equals Total Calls while still showing how much of it connected. Replaces the previous
// smoothed-line-over-bars chart, which read as misleading "hills" for what's actually sparse,
// spiky day-by-day counts, and whose hand-rolled label-spacing math kept breaking on long
// (weekly-bucketed) labels. Wrapped in a horizontally-scrolling container instead of shrinking
// bars/labels to fit - every bar keeps a readable minimum width no matter the range length.
const CompletedCallsBreakdownChart = ({ points = [], direction = "both" }) => {
  const showInbound = direction !== "outbound";
  const showOutbound = direction !== "inbound";

  const chartData = useMemo(
    () =>
      (Array.isArray(points) ? points : []).map((point) => ({
        key: point.key,
        label: point.label,
        inbound: point.inbound || 0,
        outbound: point.outbound || 0,
        remaining: Math.max(0, (point.total || 0) - (point.inbound || 0) - (point.outbound || 0)),
        total: point.total || 0,
      })),
    [points],
  );

  const showEmptyState =
    chartData.length === 0 || chartData.every((point) => point.total === 0);
  const rangeCaption =
    chartData.length > 1
      ? `${chartData[0].label} - ${chartData[chartData.length - 1].label}`
      : chartData[0]?.label || "";
  const chartPixelWidth = Math.max(CHART_MIN_WIDTH, chartData.length * CHART_SLOT_WIDTH);

  if (showEmptyState) {
    return (
      <div className="dash-comparison-chart is-empty">
        <div className="dash-chart-empty">
          No calls {rangeCaption ? `for ${rangeCaption}` : "in the selected date range"}.
        </div>
      </div>
    );
  }

  return (
    <div className="dash-comparison-chart">
      <div className="dash-comparison-chart__scroll">
        <div className="dash-comparison-chart__inner" style={{ width: chartPixelWidth }}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }} barCategoryGap="30%">
              <CartesianGrid vertical={false} stroke="rgba(27, 23, 37, 0.1)" />
              <XAxis
                dataKey="label"
                interval={0}
                tickLine={false}
                axisLine={{ stroke: "rgba(27, 23, 37, 0.16)" }}
                tick={{ fontSize: 11, fontWeight: 600, fill: "rgba(27, 23, 37, 0.6)" }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={40}
                tick={{ fontSize: 11, fontWeight: 600, fill: "rgba(27, 23, 37, 0.6)" }}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(27, 23, 37, 0.06)" }} />
              {showInbound ? (
                <Bar dataKey="inbound" name="Inbound Completed" stackId="calls" fill="#3B82F6" />
              ) : null}
              {showOutbound ? (
                <Bar dataKey="outbound" name="Outbound Completed" stackId="calls" fill="#5B9429" />
              ) : null}
              <Bar
                dataKey="remaining"
                name="Total Calls"
                stackId="calls"
                fill="rgba(27, 23, 37, 0.18)"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export const Dashboard = ({
  setSideBarLinkActive,
  setDashboardUrl,
}) => {
  const {
    usersPostRequest,
    authorizedGetRequest,
    PracticeId,
    token,
    setToken,
    setActiveTab,
    outBoundContact,
    inboundContact,
    practiceCreateDate,
    setUserType,
    timezonePreferenceVersion,
  } = useContextStateManagement();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [recentDaily, setRecentDaily] = useState([]);
  const [conversationsList, setConversationsList] = useState([]);
  const [leadsList, setLeadsList] = useState([]);
  // "Right now" active-call counts from the backend's voiceOpsActiveSessions definition (same
  // one Voice Ops itself uses) - independent of the date-range filter, since "in progress" is a
  // live snapshot, not a historical metric. Feeds inboundLiveSummary/outboundLiveSummary's
  // "active" field below instead of deriving it from scopedConversations' TwilioLogs-based
  // status.
  const [liveActivity, setLiveActivity] = useState({ inbound: 0, outbound: 0 });
  const [breakdownFilter, setBreakdownFilter] = useState(null);
  // Avg duration on the Inbound/Outbound Activity cards, and the Call Duration KPI card,
  // default to Actual (real talk time) and flip to Telephony (Twilio per-leg-rounded) on
  // click - each card flips independently and resets to Actual on reload.
  const [showBillableInboundAvg, setShowBillableInboundAvg] = useState(false);
  const [showBillableOutboundAvg, setShowBillableOutboundAvg] = useState(false);
  const [showBillableCallDuration, setShowBillableCallDuration] = useState(false);
  // The "Leads" KPI card is explicitly and only the outbound calling list (its own UI label
  // says "Outbound list") - this used to instead follow currentMemberListName(activeTab), the
  // shared Inbound/Outbound tab-selection state from elsewhere in the app, so the card could
  // silently start counting the Inbound upload table depending on unrelated navigation history.
  const memberListName = "LeadGenerationUploadLead";
  const [startDate, setStartDate] = useState(() => getDefaultStartDate());
  const [endDate, setEndDate] = useState(() => localDateKey(new Date()));
  const [directionFilter, setDirectionFilter] = useState("both");
  // Outbound-only view swaps the (otherwise redundant, always-zeroed) Inbound Activity card for a
  // Queue Snapshot instead - practice-wide/all-time, same endpoint OutboundOverview.jsx's Queue
  // panel already polls, not scoped to the dashboard's own date range.
  const [queueSnapshot, setQueueSnapshot] = useState(null);
  // Client-Alert-Tickets overview: practice-wide/all-time Slack follow-up ticket metrics, same
  // shape as the Outbound Queue Snapshot above - not scoped to this page's own date range.
  const [ticketOverview, setTicketOverview] = useState(null);
  // The practice's own configured timezone, resolved server-side alongside the date-filtered
  // rows above - rendering/bucketing their timestamps in this same zone (instead of the
  // browser's local zone) is what keeps "what day" a call falls on agreeing with the filter.
  const [practiceTimezone, setPracticeTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  // The practice's own configured zone specifically (distinct from practiceTimezone above,
  // which is now the viewer's own effective/local zone) - only used to show alongside a call
  // leg's local-equivalent time in the Calls Breakdown dialog.
  const [fixedTimezone, setFixedTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const practiceMinDate = useMemo(
    () => toDateInputValue(practiceCreateDate),
    [practiceCreateDate],
  );
  // The date picker can be cleared back to "" (see DateRangePicker's clear button) - when
  // that happens, /api/chartsData falls back to "practice creation date through today"
  // instead of returning nothing, so every card on this page is already showing all-time
  // data at that point. hasDateRange drives the wording of every info popup and the
  // headline below, so the page says what it's actually showing instead of always
  // implying a date filter is active.
  const hasDateRange = Boolean(startDate && endDate);

  useEffect(() => {
    if (setDashboardUrl) {
      setDashboardUrl(window.location.href);
    }
  }, [setDashboardUrl]);

  useEffect(() => {
    if (setSideBarLinkActive) {
      setSideBarLinkActive("dashboard");
    }
  }, [setSideBarLinkActive]);

  useEffect(() => {
    if (!practiceMinDate) return;

    const defaultStartDate =
      practiceMinDate > getDefaultStartDate()
        ? practiceMinDate
        : getDefaultStartDate();

    setStartDate((current) => (!current || current < practiceMinDate ? defaultStartDate : current));
    setEndDate((current) => (!current || current < practiceMinDate ? practiceMinDate : current));
  }, [practiceMinDate]);

  useEffect(() => {
    if (startDate && endDate && endDate < startDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  const fetchStats = async () => {
    if (!PracticeId || !token) return;
    setLoading(true);
    setError("");
    try {
      const body = {
        PracticeId,
        memberListName,
        outBoundContact,
        inboundContact,
        startDate,
        endDate,
      };
      const data = await usersPostRequest(token, "/api/chartsData", body);
      if (data === "Token is expired, login again") {
        setToken("");
        setUserType("client");
        dispatch(logout());
        navigate("/login");
        return;
      }
      if (!data || typeof data !== "object" || !data.totals) {
        setStats(null);
        setRecentDaily([]);
        setError(
          typeof data === "string" && data.trim()
            ? data
            : "Failed to load dashboard data",
        );
        return;
      }
      setStats(data?.totals || null);
      setRecentDaily(data?.recentDaily || []);
      setConversationsList(Array.isArray(data?.conversationsList) ? data.conversationsList : []);
      setLeadsList(Array.isArray(data?.leadsList) ? data.leadsList : []);
      setLiveActivity({
        inbound: Number(data?.liveActivity?.inbound) || 0,
        outbound: Number(data?.liveActivity?.outbound) || 0,
      });
      if (data?.practiceTimezone) {
        setPracticeTimezone(data.practiceTimezone);
      }
      if (data?.fixedTimezone) {
        setFixedTimezone(data.fixedTimezone);
      }
    } catch (err) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // timezonePreferenceVersion: bumped when the user saves a new timezone in Settings, so this
    // refetches and picks up the new practiceTimezone immediately instead of needing a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, memberListName, outBoundContact, inboundContact, startDate, endDate, timezonePreferenceVersion]);

  useEffect(() => {
    if (directionFilter !== "outbound" || !PracticeId || !token) {
      setQueueSnapshot(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await authorizedGetRequest(token, "/api/outboundQueueStatus", { PracticeId });
        if (!cancelled) {
          setQueueSnapshot(data);
        }
      } catch (error) {
        if (!cancelled) {
          setQueueSnapshot(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // authorizedGetRequest intentionally excluded - ContextApi.js doesn't memoize its functions,
    // so including it would re-fetch on every unrelated context re-render (same rationale as
    // OutboundOverview.jsx's QueuePanel/apiKpiTotals effects).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directionFilter, PracticeId, token]);

  useEffect(() => {
    if (!PracticeId || !token) {
      setTicketOverview(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await authorizedGetRequest(token, "/api/client-alert-tickets/overview", {
          PracticeId,
        });
        if (!cancelled) {
          setTicketOverview(data);
        }
      } catch (error) {
        if (!cancelled) {
          setTicketOverview(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // authorizedGetRequest intentionally excluded - see the Outbound Queue effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token]);

  const conversationStats = stats?.conversations || stats?.calls || {};
  // Headline KPIs (this card, the status breakdown, billable minutes, the trend graph) are scoped
  // to the practice's number as configured TODAY - conversationsList itself stays the full,
  // widened set (every historical number too) so Call Breakdown below can still show everything;
  // only this narrowed view feeds the summary numbers above it.
  const narrowConversationsList = useMemo(
    () => conversationsList.filter((c) => c?.isCurrentNumber),
    [conversationsList],
  );
  const scopedConversations = useMemo(
    () => filterConversationsByDirection(narrowConversationsList, directionFilter),
    [narrowConversationsList, directionFilter],
  );

  const directedStatusCounts = useMemo(() => {
    const counts = {
      total: 0,
      completed: 0,
      voicemail: 0,
      failed: 0,
      noAnswer: 0,
      busy: 0,
      missed: 0,
      canceled: 0,
      otherStatus: 0,
    };

    if (scopedConversations.length === 0) {
      return counts;
    }

    scopedConversations.forEach((row) => {
      counts.total += 1;

      // "Missed" is a separate, inbound-specific category layered on top of no_answer/busy -
      // without excluding those calls from noAnswer/busy too, the same call landed in both a
      // status-breakdown tile AND "Missed" simultaneously, so the tiles never summed to total
      // the way a "breakdown" implies they should.
      const isInboundMissed =
        row?.direction === "inbound" && (row?.status === "no_answer" || row?.rawStatus === "busy");
      // Same reasoning as "Missed" above - canceled calls fall through getStatusCategoryFromValue
      // into status "other" (there's no dedicated "canceled" status category), so without this
      // check first, every canceled call was being counted in BOTH "Cancelled" AND "Other"
      // instead of just "Cancelled".
      const isCanceled = row?.rawStatus === "canceled";

      if (isInboundMissed) {
        counts.missed += 1;
      } else if (isCanceled) {
        counts.canceled += 1;
      } else if (row?.status === "completed") counts.completed += 1;
      else if (row?.status === "voicemail") counts.voicemail += 1;
      else if (row?.status === "failed") counts.failed += 1;
      else if (row?.status === "no_answer") counts.noAnswer += 1;
      else if (row?.rawStatus === "busy") counts.busy += 1;
      else counts.otherStatus += 1;
    });

    return counts;
  }, [scopedConversations]);

  const displayStats = useMemo(() => {
    const base = conversationStats || {};
    if (directionFilter === "inbound") {
      return {
        ...base,
        total: base.inbound ?? 0,
        durationSec: base.inboundDurationSec ?? 0,
      };
    }
    if (directionFilter === "outbound") {
      return {
        ...base,
        total: base.outbound ?? 0,
        durationSec: base.outboundDurationSec ?? 0,
      };
    }
    return base;
  }, [conversationStats, directionFilter]);

  const billableMinutes = useMemo(() => {
    const all = Array.isArray(narrowConversationsList) ? narrowConversationsList : [];
    const inboundAll = all.filter((conversation) => conversation?.direction === "inbound");
    const outboundAll = all.filter((conversation) => conversation?.direction === "outbound");
    const totalAll = sumLegMinutes(all);
    const inbound = sumLegMinutes(inboundAll);
    const outbound = sumLegMinutes(outboundAll);
    const sumTransferMinutes = (list) =>
      list.reduce((sum, conversation) => {
        const legs = Array.isArray(conversation?.legs) ? conversation.legs : [];
        return (
          sum +
          legs
            .filter((leg) => leg?.role === "transfer" || leg?.legType === "transfer")
            .reduce(
              (legSum, leg) =>
                legSum + Math.max(0, Math.ceil((Number(leg?.durationSec) || 0) / 60)),
              0,
            )
        );
      }, 0);

    // Raw, unrounded transfer-leg seconds - the "on transfers" meta line under the KPI card
    // always showed the rounded/Telephony-style number (sumTransferMinutes) even while the card
    // itself was flipped to "Actual", disagreeing with the dialog's own Actual transfer figure.
    // This is that same "Actual" number, so the meta line can flip along with the card.
    const sumTransferActualSeconds = (list) =>
      list.reduce((sum, conversation) => {
        const legs = Array.isArray(conversation?.legs) ? conversation.legs : [];
        return (
          sum +
          legs
            .filter((leg) => leg?.role === "transfer" || leg?.legType === "transfer")
            .reduce((legSum, leg) => legSum + (Number(leg?.durationSec) || 0), 0)
        );
      }, 0);

    let scoped = totalAll;
    let scopedList = all;
    if (directionFilter === "inbound") {
      scoped = inbound;
      scopedList = inboundAll;
    } else if (directionFilter === "outbound") {
      scoped = outbound;
      scopedList = outboundAll;
    }

    return {
      all: totalAll,
      inbound,
      outbound,
      scoped,
      transfer: sumTransferMinutes(scopedList),
      transferActual: Math.round(sumTransferActualSeconds(scopedList) / 60),
      actualScoped: Math.round(sumTalkTimeSeconds(scopedList) / 60),
    };
  }, [narrowConversationsList, directionFilter]);

  const completionRate = useMemo(() => {
    const total = directedStatusCounts.total || 0;
    if (!total) return 0;
    return Math.round(((directedStatusCounts.completed || 0) / total) * 100);
  }, [directedStatusCounts.total, directedStatusCounts.completed]);

  // One plain-language sentence summarizing the whole selected period, so a manager can read
  // the headline number without parsing any of the cards/charts below it.
  const dashboardHeadline = useMemo(() => {
    const total = directedStatusCounts.total || 0;
    if (!total) {
      return hasDateRange ? "No calls in this date range yet." : "No calls yet.";
    }

    const followUpCount =
      (directedStatusCounts.voicemail || 0) +
      (directedStatusCounts.noAnswer || 0) +
      (directedStatusCounts.busy || 0) +
      (directedStatusCounts.missed || 0);

    const parts = [
      `${formatCountValue(total)} call${total === 1 ? "" : "s"} ${hasDateRange ? "in this period" : "across all time"}`,
      `${completionRate}% connected`,
    ];

    if (followUpCount > 0) {
      parts.push(`${formatCountValue(followUpCount)} need${followUpCount === 1 ? "s" : ""} follow-up`);
    }

    return parts.join(" · ");
  }, [directedStatusCounts, completionRate, hasDateRange]);

  const transfersCount = useMemo(
    () =>
      scopedConversations.filter(
        (conversation) => Number(conversation?.transferLegCount || 0) > 0,
      ).length,
    [scopedConversations],
  );

  // When no range is selected, build the trend off the same practice-creation-date-through-today
  // window /api/chartsData already fell back to for every other card on this page - previously
  // this fell through to buildCompletedTrend's own "today" default instead, so the trend chart
  // showed almost nothing while the KPI cards above it were already showing all-time totals.
  const effectiveTrendStart = startDate || practiceMinDate || localDateKey(new Date());
  const effectiveTrendEnd = endDate || localDateKey(new Date());

  const completedTrend = useMemo(
    () =>
      buildCompletedTrend(scopedConversations, effectiveTrendStart, effectiveTrendEnd, practiceTimezone),
    [effectiveTrendStart, effectiveTrendEnd, scopedConversations, practiceTimezone],
  );

  const statusListItems = useMemo(
    () => [
      {
        key: "voicemail",
        label: "Voicemail",
        value: directedStatusCounts.voicemail ?? 0,
        tone: "warning",
        description: "Call was answered by voicemail.",
      },
      {
        key: "failed",
        label: "Failed",
        value: directedStatusCounts.failed ?? 0,
        tone: "danger",
        description: "Call failed to connect due to a network issue.",
      },
      {
        key: "noAnswer",
        label: "No Answer",
        value: directedStatusCounts.noAnswer ?? 0,
        tone: "warning",
        description: "Outgoing call that rang without being answered.",
      },
      {
        key: "busy",
        label: "Busy",
        value: directedStatusCounts.busy ?? 0,
        tone: "muted",
        description: "Outgoing call that reached a busy line.",
      },
      {
        key: "missed",
        label: "Missed",
        value: directedStatusCounts.missed ?? 0,
        tone: "danger",
        description: "Incoming call that went unanswered.",
      },
      {
        key: "canceled",
        label: "Cancelled",
        value: directedStatusCounts.canceled ?? 0,
        tone: "muted",
        description: "Call was cancelled before connecting.",
      },
      {
        key: "other",
        label: "Other",
        value: directedStatusCounts.otherStatus ?? 0,
        tone: "muted",
        description: "Call ended in an uncategorized status.",
      },
    ],
    [directedStatusCounts],
  );

  // Prefixes panel titles ("Call Status Overview" -> "Inbound Call Status Overview") once a
  // single direction is selected, so it's unambiguous the numbers below are a subset instead of
  // reading as "the same totals, just smaller."
  const directionLabel =
    directionFilter === "inbound" ? "Inbound " : directionFilter === "outbound" ? "Outbound " : "";

  // Inbound-only view swaps the (otherwise redundant, always-zeroed) Outbound Activity card for
  // this instead - reuses directedStatusCounts, which is already scoped to inbound-only here.
  const missedCallSummary = useMemo(() => {
    const total = directedStatusCounts.total || 0;
    const missed = directedStatusCounts.missed || 0;
    const voicemail = directedStatusCounts.voicemail || 0;
    return {
      total,
      missed,
      voicemail,
      missedRate: total ? Math.round((missed / total) * 100) : 0,
    };
  }, [directedStatusCounts]);

  const directedRecentDaily = useMemo(() => {
    if (directionFilter === "both") return recentDaily;
    if (scopedConversations.length === 0) return [];

    const byDay = new Map();

    scopedConversations.forEach((row) => {
      const sourceDate = row?.startedAt;
      if (!sourceDate) return;

      const dayKey = new Date(sourceDate).toISOString().slice(0, 10);
      const current = byDay.get(dayKey) || {
        date: dayKey,
        conversations: 0,
        completed: 0,
      };

      current.conversations += 1;
      if (row?.status === "completed") current.completed += 1;

      byDay.set(dayKey, current);
    });

    return Array.from(byDay.values())
      .sort((left, right) => right.date.localeCompare(left.date))
      .slice(0, 14)
      .map((row) => ({ ...row, calls: row.conversations }));
  }, [directionFilter, recentDaily, scopedConversations]);

  const maxDaily = useMemo(() => {
    if (!directedRecentDaily.length) return 1;
    return Math.max(
      ...directedRecentDaily.map((day) => day.conversations ?? day.calls ?? 0),
      1,
    );
  }, [directedRecentDaily]);

  const barWidth = (value) =>
    `${Math.max(8, Math.round(((value || 0) / maxDaily) * 100))}%`;

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    setStartDate(nextStartDate || "");
    setEndDate(nextEndDate || "");
  };

  const transferSummary = useMemo(() => {
    const todayKey = practiceDateKey(new Date(), practiceTimezone);
    const transferCalls = scopedConversations.filter(
      (conversation) => Number(conversation?.transferLegCount || 0) > 0,
    );
    const todayTransfers = transferCalls.filter(
      (conversation) => practiceDateKey(conversation?.startedAt, practiceTimezone) === todayKey,
    ).length;
    const inboundTransfers = transferCalls.filter(
      (conversation) => conversation?.direction === "inbound",
    ).length;
    const outboundTransfers = transferCalls.filter(
      (conversation) => conversation?.direction === "outbound",
    ).length;
    const successfulTransfers = transferCalls.filter(
      (conversation) => conversation?.status === "completed",
    ).length;
    const successRate = transferCalls.length
      ? Math.round((successfulTransfers / transferCalls.length) * 100)
      : 0;

    return {
      total: transferCalls.length,
      today: todayTransfers,
      inbound: inboundTransfers,
      outbound: outboundTransfers,
      successful: successfulTransfers,
      successRate,
    };
  }, [scopedConversations, practiceTimezone]);

  const inboundLiveSummary = useMemo(() => {
    const inboundCalls = scopedConversations.filter(
      (conversation) => conversation?.direction === "inbound",
    );
    const total = inboundCalls.length;
    const completed = inboundCalls.filter(
      (conversation) => conversation?.status === "completed",
    ).length;
    // Avg Duration divides by "answered" (completed + voicemail), matching
    // InboundOverview.jsx/OutboundOverview.jsx's convention - dividing by completed-only counted
    // voicemail calls' duration in the numerator but excluded them from the denominator, inflating
    // the average whenever a practice had voicemail-status calls with real duration.
    const voicemailCount = inboundCalls.filter(
      (conversation) => conversation?.status === "voicemail",
    ).length;
    const answered = completed + voicemailCount;
    const billableMinutesTotal = inboundCalls.reduce(
      (sum, conversation) => sum + Math.max(0, Math.ceil((Number(conversation?.billableSec) || 0) / 60)),
      0,
    );
    const billableAvgMinutes = answered ? Math.round(billableMinutesTotal / answered) : 0;
    const actualAvgMinutes = answered
      ? Math.round(sumTalkTimeSeconds(inboundCalls) / answered / 60)
      : 0;

    return {
      active: liveActivity.inbound,
      completed,
      avgDurationBillable: formatMinutesValue(billableAvgMinutes),
      avgDurationActual: `${Math.max(0, actualAvgMinutes)} min`,
      total,
    };
  }, [scopedConversations, liveActivity.inbound]);

  const outboundLiveSummary = useMemo(() => {
    const outboundCalls = scopedConversations.filter(
      (conversation) => conversation?.direction === "outbound",
    );
    const total = outboundCalls.length;
    const completed = outboundCalls.filter(
      (conversation) => conversation?.status === "completed",
    ).length;
    // Avg Duration divides by "answered" (completed + voicemail) - see identical note in
    // inboundLiveSummary above.
    const voicemailCount = outboundCalls.filter(
      (conversation) => conversation?.status === "voicemail",
    ).length;
    const answered = completed + voicemailCount;
    const billableMinutesTotal = outboundCalls.reduce(
      (sum, conversation) => sum + Math.max(0, Math.ceil((Number(conversation?.billableSec) || 0) / 60)),
      0,
    );
    const billableAvgMinutes = answered ? Math.round(billableMinutesTotal / answered) : 0;
    const actualAvgMinutes = answered
      ? Math.round(sumTalkTimeSeconds(outboundCalls) / answered / 60)
      : 0;

    return {
      active: liveActivity.outbound,
      completed,
      avgDurationBillable: formatMinutesValue(billableAvgMinutes),
      avgDurationActual: `${Math.max(0, actualAvgMinutes)} min`,
      total,
    };
  }, [scopedConversations, liveActivity.outbound]);

  const busiestHours = useMemo(
    () => buildBusiestHours(scopedConversations, practiceTimezone),
    [scopedConversations, practiceTimezone],
  );

  const satisfactionRows = useMemo(() => {
    const total = directedStatusCounts.total || 0;
    const followUpCount =
      (directedStatusCounts.voicemail || 0) +
      (directedStatusCounts.noAnswer || 0) +
      (directedStatusCounts.busy || 0) +
      (directedStatusCounts.missed || 0);
    const unsuccessfulCount =
      (directedStatusCounts.failed || 0) +
      (directedStatusCounts.canceled || 0) +
      (directedStatusCounts.otherStatus || 0);
    const positiveCount = directedStatusCounts.completed || 0;
    const toPercent = (value) => (total ? Math.round((value / total) * 100) : 0);

    return [
      {
        key: "positive",
        label: "Positive outcome",
        value: positiveCount,
        meta: `${toPercent(positiveCount)}% of total`,
        tone: "good",
      },
      {
        key: "followup",
        label: "Needs follow-up",
        value: followUpCount,
        meta: `${toPercent(followUpCount)}% of total`,
        tone: "warning",
      },
      {
        key: "unsuccessful",
        label: "Unsuccessful",
        value: unsuccessfulCount,
        meta: `${toPercent(unsuccessfulCount)}% of total`,
        tone: "danger",
      },
    ];
  }, [directedStatusCounts]);

  // Every number on this page comes from the same backend response, which falls back to
  // "practice creation date through today" whenever startDate/endDate are empty - these
  // info-popup sentences swap their date-scope clause to match that, instead of always
  // claiming "the selected date range" even when no range is actually selected. The
  // Outbound Queue card is intentionally excluded - that one is practice-wide/all-time
  // regardless of this page's date filter, so its wording never changes.
  const tooltipCopy = useMemo(() => {
    const inRange = "in the selected date range";
    const allTime = "across all time";
    const scope = hasDateRange ? inRange : allTime;
    const forScope = hasDateRange ? "for the selected date range" : "across all time";

    return {
      totalCalls: `All calls made or received ${scope}.`,
      callDuration: `Total time spent on calls ${scope}, including transfers.`,
      leads: `Contacts on the outbound calling list ${scope}.`,
      completionRate: `Percentage of calls that connected ${scope}.`,
      callVolumeTrend: `Daily call volume ${forScope}, with completed calls highlighted.`,
      callStatusOverview: hasDateRange
        ? "Breakdown of how calls in this date range ended."
        : "Breakdown of how calls ended, across all time.",
      transfers: `Calls passed to another person or line ${scope}.`,
      inboundActivity: `Summary of incoming call activity ${forScope}, as of the last page load.`,
      outboundActivity: `Summary of outgoing call activity ${forScope}, as of the last page load.`,
      inboundMissed: `Percentage of incoming calls that went unanswered ${scope}.`,
      busiestHours: hasDateRange
        ? "The hours with the highest call volume in the selected date range."
        : "The hours with the highest call volume, across all time.",
      callSatisfaction: `Calls grouped by outcome ${scope}: connected, needs follow up, or unsuccessful.`,
    };
  }, [hasDateRange]);

  return (
    <div className="dash-shell">
      <div className="dash-header">
        <div className="dash-toolbar">
          <div className="dash-toolbar-range">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onRangeChange={handleDateRangeChange}
            />
            {!hasDateRange ? (
              <span className="dash-toolbar-alltime-note">Showing all-time data</span>
            ) : null}
          </div>
          <label className="dash-toolbar-view">
            <span className="dash-toolbar-view-label">View</span>
            <select
              value={directionFilter}
              onChange={(event) => setDirectionFilter(event.target.value)}
              className="dash-filter-input"
            >
              <option value="both">Both</option>
              <option value="inbound">Inbound only</option>
              <option value="outbound">Outbound only</option>
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="dash-banner dash-banner-info">Loading dashboard...</div>
      ) : null}
      {error ? (
        <div className="dash-banner dash-banner-error">{error}</div>
      ) : null}

      {!loading && stats ? (
        <>
          <div className="dash-headline">
            <p className="dash-headline__text">{dashboardHeadline}</p>
          </div>

          <div className="dash-kpi-grid">
            <div
              className="dash-kpi-card dash-kpi-primary"
              role="button"
              tabIndex={0}
              onClick={() => setBreakdownFilter("all")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setBreakdownFilter("all");
                }
              }}
            >
              <span className="dash-kpi-label">
                Total Calls
                <InfoTooltip text={tooltipCopy.totalCalls} />
              </span>
              <div className="dash-kpi-value">{displayStats.total ?? 0}</div>
              <div className="dash-kpi-meta">
                <span>Click to view all</span>
              </div>
            </div>

            <div
              className="dash-kpi-card"
              role="button"
              tabIndex={0}
              onClick={() => setBreakdownFilter("billable")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setBreakdownFilter("billable");
                }
              }}
            >
              <span className="dash-kpi-label">
                Call Duration
                <InfoTooltip text={tooltipCopy.callDuration} />
                <span className="dash-summary-stat__mode-tag">
                  {showBillableCallDuration ? "Telephony talk time" : "Talk time"}
                </span>
              </span>
              <div
                className={`dash-kpi-value dash-duration-flip${showBillableCallDuration ? " dash-duration-flip--flipped" : ""}`}
                role="button"
                tabIndex={0}
                title="Click to flip between Actual and Telephony minutes"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowBillableCallDuration((prev) => !prev);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowBillableCallDuration((prev) => !prev);
                  }
                }}
              >
                <RotateCw className="dash-duration-flip__icon" size={12} strokeWidth={2.4} aria-hidden="true" />
                <div className="dash-duration-flip__inner">
                  <span className="dash-duration-flip__face">{billableMinutes.actualScoped} min</span>
                  <span className="dash-duration-flip__face dash-duration-flip__face--back">
                    {billableMinutes.scoped} min
                  </span>
                </div>
              </div>
              <div className="dash-kpi-meta">
                <span>
                  {transfersCount > 0
                    ? `+${transfersCount} transfer${transfersCount === 1 ? "" : "s"}`
                    : "No transfers"}
                </span>
                {(showBillableCallDuration ? billableMinutes.transfer : billableMinutes.transferActual) > 0 ? (
                  <>
                    <span className="dash-kpi-divider">•</span>
                    <span>
                      {formatMinutesValue(
                        showBillableCallDuration ? billableMinutes.transfer : billableMinutes.transferActual,
                      )}{" "}
                      on transfers
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            {directionFilter !== "inbound" ? (
              <div
                className="dash-kpi-card"
                role="button"
                tabIndex={0}
                onClick={() => setBreakdownFilter("leads")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    setBreakdownFilter("leads");
                  }
                }}
              >
                <span className="dash-kpi-label">
                  Leads
                  <InfoTooltip text={tooltipCopy.leads} />
                </span>
                <div className="dash-kpi-value">{stats.leads?.total ?? 0}</div>
                <div className="dash-kpi-meta">
                  <span>{stats.leads?.withStatus ?? 0} with status</span>
                  <span className="dash-kpi-divider">•</span>
                  <span>Outbound list</span>
                </div>
              </div>
            ) : null}

            <div
              className="dash-kpi-card"
              role="button"
              tabIndex={0}
              onClick={() => setBreakdownFilter("completed")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setBreakdownFilter("completed");
                }
              }}
            >
              <span className="dash-kpi-label">
                Completion Rate
                <InfoTooltip text={tooltipCopy.completionRate} />
              </span>
              <div className={`dash-kpi-value dash-kpi-value--${toneForRate(completionRate)}`}>
                {completionRate}%
              </div>
              <div className={`dash-kpi-progress dash-kpi-progress--${toneForRate(completionRate)}`}>
                <span style={{ width: `${completionRate}%` }} />
              </div>
              <div className="dash-kpi-meta">
                <span>
                  {directedStatusCounts.completed ?? 0} of {directedStatusCounts.total ?? 0}
                </span>
              </div>
            </div>
          </div>

          {ticketOverview ? (
            <>
              <h3 className="dash-panel-title">Client Alert Tickets</h3>
              <div className="dash-kpi-grid">
                <div className="dash-kpi-card">
                  <span className="dash-kpi-label">Open Alert Tickets</span>
                  <div className="dash-kpi-value">
                    {formatCountValue(ticketOverview.openCount)}
                  </div>
                </div>

                <div className="dash-kpi-card">
                  <span className="dash-kpi-label">Completed Alert Tickets</span>
                  <div className="dash-kpi-value">
                    {formatCountValue(ticketOverview.completedCount)}
                  </div>
                </div>

                <div className="dash-kpi-card">
                  <span className="dash-kpi-label">Oldest Open Ticket Age</span>
                  <div className="dash-kpi-value">
                    {ticketOverview.oldestOpenTicketAgeHours === null ||
                    ticketOverview.oldestOpenTicketAgeHours === undefined
                      ? "—"
                      : `${ticketOverview.oldestOpenTicketAgeHours.toFixed(1)} hrs`}
                  </div>
                </div>

                <div className="dash-kpi-card">
                  <span className="dash-kpi-label">Alert Ticket Completion Rate</span>
                  <div
                    className={`dash-kpi-value dash-kpi-value--${toneForRate(
                      ticketOverview.completionRate === null
                        ? 0
                        : Math.round(ticketOverview.completionRate * 100),
                    )}`}
                  >
                    {ticketOverview.completionRate === null
                      ? "—"
                      : `${Math.round(ticketOverview.completionRate * 100)}%`}
                  </div>
                </div>
              </div>
            </>
          ) : null}

          <div className="dash-analytics-grid">
            <div className="dash-panel dash-breakdown-panel dash-trend-panel">
              <div className="dash-trend-panel__header">
                <div className="dash-trend-panel__header-copy">
                  <h3 className="dash-panel-title">
                    {directionLabel}Call Volume Trend
                    <InfoTooltip text={tooltipCopy.callVolumeTrend} />
                  </h3>
                  <p className="dash-panel-subtitle">
                    {directionFilter === "both"
                      ? "Inbound vs Outbound performance"
                      : "Total calls, with completed calls highlighted"}
                  </p>
                </div>
                <div className="dash-legend dash-trend-panel__legend">
                  <span className="dash-legend-item">
                    <span className="dash-legend-swatch dash-legend-swatch--total" />
                    Total Calls
                  </span>
                  {directionFilter !== "outbound" ? (
                    <span className="dash-legend-item">
                      <span className="dash-legend-swatch dash-legend-swatch--inbound" />
                      Inbound Completed
                    </span>
                  ) : null}
                  {directionFilter !== "inbound" ? (
                    <span className="dash-legend-item">
                      <span className="dash-legend-swatch dash-legend-swatch--outbound" />
                      Outbound Completed
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="dash-trend-panel__chart">
                <CompletedCallsBreakdownChart points={completedTrend} direction={directionFilter} />
              </div>
            </div>

            <div className="dash-panel dash-panel--status">
              <div className="dash-panel-header">
                <div className="dash-panel-header-copy">
                  <h3 className="dash-panel-title">
                    {directionLabel}Call Status Overview
                    <InfoTooltip text={tooltipCopy.callStatusOverview} />
                  </h3>
                  <p className="dash-panel-subtitle">
                    What's happening with these calls
                  </p>
                </div>
              </div>
              <div className="dash-status-list">
                {statusListItems.map((item) => (
                  <div
                    className="dash-status-list-item"
                    key={item.key}
                    role="button"
                    tabIndex={0}
                    title={item.description || undefined}
                    onClick={() => setBreakdownFilter(item.key)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        setBreakdownFilter(item.key);
                      }
                    }}
                  >
                    <div className="dash-status-list-content">
                      <span className={`dash-status-list-tone dash-status-list-tone--${item.tone}`} />
                      <span className="dash-status-list-label">{item.label}</span>
                    </div>
                    <span className="dash-status-list-value">{item.value}</span>
                  </div>
                ))}
                {typeof conversationStats.droppedCalls === "number" ? (
                  <div
                    className="dash-status-list-item dash-status-list-item--summary"
                    title="Calls that connected but never reached a resolution - no transfer was ever answered."
                  >
                    <div className="dash-status-list-content">
                      <span className="dash-status-list-tone dash-status-list-tone--danger" />
                      <span className="dash-status-list-label">Dropped Calls</span>
                    </div>
                    <span className="dash-status-list-value">{conversationStats.droppedCalls}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="dash-summary-grid">
            <div
              className="dash-summary-card dash-transfers-summary"
              role="button"
              tabIndex={0}
              onClick={() => setBreakdownFilter("transfers")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setBreakdownFilter("transfers");
                }
              }}
            >
              <div className="dash-summary-card__header">
                <h3 className="dash-side-card-title">
                  Transfers
                  <InfoTooltip text={tooltipCopy.transfers} />
                </h3>
                <span className="dash-transfers-arrow">View</span>
              </div>
              <div className="dash-summary-card__hero">
                <span className="dash-summary-card__hero-label">Total today</span>
                <strong className="dash-summary-card__hero-value">
                  {transferSummary.today}
                </strong>
                <span className="dash-summary-card__hero-meta">
                  {transferSummary.total} {hasDateRange ? "in selected range" : "across all time"}
                </span>
              </div>
              {directionFilter === "both" ? (
                <div className="dash-summary-card__split">
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Inbound</span>
                    <strong className="dash-summary-stat__value">
                      {transferSummary.inbound}
                    </strong>
                  </div>
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Outbound</span>
                    <strong className="dash-summary-stat__value">
                      {transferSummary.outbound}
                    </strong>
                  </div>
                </div>
              ) : null}
              <div className="dash-summary-card__footer">
                <div className="dash-summary-progress-copy">
                  <span className="dash-summary-stat__label">Successful handoff</span>
                  <span className="dash-summary-progress-value">
                    {transferSummary.successful}/{transferSummary.total || 0}
                  </span>
                </div>
                <div
                  className={`dash-kpi-progress dash-summary-progress dash-kpi-progress--${toneForRate(transferSummary.successRate)}`}
                >
                  <span style={{ width: `${transferSummary.successRate}%` }} />
                </div>
              </div>
            </div>

            {directionFilter !== "outbound" ? (
              <div className="dash-summary-card dash-live-card">
                <div className="dash-summary-card__header">
                  <h3 className="dash-side-card-title">
                    Inbound Activity
                    <InfoTooltip text={tooltipCopy.inboundActivity} />
                  </h3>
                  <span
                    className={`dash-live-pill${inboundLiveSummary.active > 0 ? " dash-live-pill--active" : ""}`}
                  >
                    {inboundLiveSummary.active > 0 ? "In Progress" : "Idle"}
                  </span>
                </div>
                <div className="dash-summary-card__hero">
                  <span className="dash-summary-card__hero-label">In progress</span>
                  <strong
                    className={`dash-summary-card__hero-value${
                      inboundLiveSummary.active > 0 ? " dash-summary-card__hero-value--good" : ""
                    }`}
                  >
                    {inboundLiveSummary.active}
                  </strong>
                </div>
                <div className="dash-summary-card__split">
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Completed</span>
                    <strong className="dash-summary-stat__value">
                      {inboundLiveSummary.completed}
                    </strong>
                  </div>
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">
                      Avg duration
                      <span className="dash-summary-stat__mode-tag">
                        {showBillableInboundAvg ? "Telephony talk time" : "Talk time"}
                      </span>
                    </span>
                    <div
                      className={`dash-duration-flip${showBillableInboundAvg ? " dash-duration-flip--flipped" : ""}`}
                      role="button"
                      tabIndex={0}
                      title="Click to flip between Actual and Telephony minutes"
                      onClick={() => setShowBillableInboundAvg((prev) => !prev)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShowBillableInboundAvg((prev) => !prev);
                        }
                      }}
                    >
                      <RotateCw className="dash-duration-flip__icon" size={11} strokeWidth={2.4} aria-hidden="true" />
                      <div className="dash-duration-flip__inner">
                        <strong className="dash-summary-stat__value dash-duration-flip__face">
                          {inboundLiveSummary.avgDurationActual}
                        </strong>
                        <strong className="dash-summary-stat__value dash-duration-flip__face dash-duration-flip__face--back">
                          {inboundLiveSummary.avgDurationBillable}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dash-summary-card__footer dash-summary-card__footer--single">
                  <span className="dash-summary-stat__label">Total calls</span>
                  <strong className="dash-summary-card__footer-value">
                    {inboundLiveSummary.total}
                  </strong>
                </div>
              </div>
            ) : (
              // Outbound-only: the Inbound Activity card would just be an empty 0/0/0 box, so show
              // the outbound queue snapshot here instead (see queueSnapshot's fetch effect above).
              <div className="dash-summary-card dash-live-card">
                <div className="dash-summary-card__header">
                  <h3 className="dash-side-card-title">
                    Outbound Queue
                    <InfoTooltip text="Current status of the outbound calling list." />
                  </h3>
                  <span className="dash-live-pill">Queue</span>
                </div>
                <div className="dash-summary-card__hero">
                  <span className="dash-summary-card__hero-label">Not yet called</span>
                  <strong className="dash-summary-card__hero-value">
                    {formatCountValue(queueSnapshot?.pending)}
                  </strong>
                </div>
                <div className="dash-summary-card__split">
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Attempted</span>
                    <strong className="dash-summary-stat__value">
                      {formatCountValue(queueSnapshot?.called)}
                    </strong>
                  </div>
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Completed</span>
                    <strong className="dash-summary-stat__value">
                      {formatCountValue(queueSnapshot?.completed)}
                    </strong>
                  </div>
                </div>
                <div className="dash-summary-card__footer dash-summary-card__footer--single">
                  <span className="dash-summary-stat__label">Calling now</span>
                  <strong className="dash-summary-card__footer-value">
                    {formatCountValue(queueSnapshot?.callingNow)}
                  </strong>
                </div>
              </div>
            )}

            {directionFilter !== "inbound" ? (
              <div className="dash-summary-card dash-live-card">
                <div className="dash-summary-card__header">
                  <h3 className="dash-side-card-title">
                    Outbound Activity
                    <InfoTooltip text={tooltipCopy.outboundActivity} />
                  </h3>
                  <span
                    className={`dash-live-pill${outboundLiveSummary.active > 0 ? " dash-live-pill--active" : ""}`}
                  >
                    {outboundLiveSummary.active > 0 ? "In Progress" : "Idle"}
                  </span>
                </div>
                <div className="dash-summary-card__hero">
                  <span className="dash-summary-card__hero-label">In progress</span>
                  <strong
                    className={`dash-summary-card__hero-value${
                      outboundLiveSummary.active > 0 ? " dash-summary-card__hero-value--good" : ""
                    }`}
                  >
                    {outboundLiveSummary.active}
                  </strong>
                </div>
                <div className="dash-summary-card__split">
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Completed</span>
                    <strong className="dash-summary-stat__value">
                      {outboundLiveSummary.completed}
                    </strong>
                  </div>
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">
                      Avg duration
                      <span className="dash-summary-stat__mode-tag">
                        {showBillableOutboundAvg ? "Telephony talk time" : "Talk time"}
                      </span>
                    </span>
                    <div
                      className={`dash-duration-flip${showBillableOutboundAvg ? " dash-duration-flip--flipped" : ""}`}
                      role="button"
                      tabIndex={0}
                      title="Click to flip between Actual and Telephony minutes"
                      onClick={() => setShowBillableOutboundAvg((prev) => !prev)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShowBillableOutboundAvg((prev) => !prev);
                        }
                      }}
                    >
                      <RotateCw className="dash-duration-flip__icon" size={11} strokeWidth={2.4} aria-hidden="true" />
                      <div className="dash-duration-flip__inner">
                        <strong className="dash-summary-stat__value dash-duration-flip__face">
                          {outboundLiveSummary.avgDurationActual}
                        </strong>
                        <strong className="dash-summary-stat__value dash-duration-flip__face dash-duration-flip__face--back">
                          {outboundLiveSummary.avgDurationBillable}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dash-summary-card__footer dash-summary-card__footer--single">
                  <span className="dash-summary-stat__label">Total calls</span>
                  <strong className="dash-summary-card__footer-value">
                    {outboundLiveSummary.total}
                  </strong>
                </div>
              </div>
            ) : (
              // Inbound-only: the Outbound Activity card would just be an empty 0/0/0 box, so show
              // the inbound missed-call rate here instead (already scoped by directedStatusCounts).
              <div className="dash-summary-card dash-live-card">
                <div className="dash-summary-card__header">
                  <h3 className="dash-side-card-title">
                    Inbound Missed
                    <InfoTooltip text={tooltipCopy.inboundMissed} />
                  </h3>
                </div>
                <div className="dash-summary-card__hero">
                  <span className="dash-summary-card__hero-label">Missed rate</span>
                  <strong
                    className={`dash-summary-card__hero-value dash-kpi-value--${toneForRate(100 - missedCallSummary.missedRate)}`}
                  >
                    {missedCallSummary.missedRate}%
                  </strong>
                </div>
                <div className="dash-summary-card__split">
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Missed</span>
                    <strong className="dash-summary-stat__value">
                      {formatCountValue(missedCallSummary.missed)}
                    </strong>
                  </div>
                  <div className="dash-summary-stat">
                    <span className="dash-summary-stat__label">Voicemail</span>
                    <strong className="dash-summary-stat__value">
                      {formatCountValue(missedCallSummary.voicemail)}
                    </strong>
                  </div>
                </div>
                <div className="dash-summary-card__footer dash-summary-card__footer--single">
                  <span className="dash-summary-stat__label">Total inbound</span>
                  <strong className="dash-summary-card__footer-value">
                    {formatCountValue(missedCallSummary.total)}
                  </strong>
                </div>
              </div>
            )}
          </div>

          <div className="dash-insight-grid">
            <div className="dash-panel dash-panel--medium">
              <div className="dash-panel-header">
                <div className="dash-panel-header-copy">
                  <h3 className="dash-panel-title">
                    {directionLabel}Busiest Hours
                    <InfoTooltip text={tooltipCopy.busiestHours} />
                  </h3>
                  <p className="dash-panel-subtitle">
                    Busiest times to call
                  </p>
                </div>
              </div>
              <div className="dash-stack-list">
                {busiestHours.length === 0 ? (
                  <div className="dash-empty">
                    {hasDateRange ? "No conversations in this date range." : "No conversations yet."}
                  </div>
                ) : null}
                {busiestHours.map((row) => (
                  <div className="dash-stack-row" key={row.key}>
                    <div className="dash-stack-row__copy">
                      <span className="dash-stack-row__label">{row.label}</span>
                      <span className="dash-stack-row__meta">{row.share}% share</span>
                    </div>
                    <div className="dash-stack-row__bar">
                      <div
                        className="dash-recent-bar-fill"
                        style={{ width: `${Math.max(10, row.share)}%` }}
                      />
                    </div>
                    <div className="dash-stack-row__value">{row.count}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dash-panel dash-panel--medium">
              <div className="dash-panel-header">
                <div className="dash-panel-header-copy">
                  <h3 className="dash-panel-title">
                    {directionLabel}Call Satisfaction
                    <InfoTooltip text={tooltipCopy.callSatisfaction} />
                  </h3>
                  <p className="dash-panel-subtitle">
                    How these calls turned out
                  </p>
                </div>
              </div>
              <div className="dash-stack-list">
                {satisfactionRows.map((row) => (
                  <div className="dash-stack-row dash-stack-row--metric" key={row.key}>
                    <div className="dash-stack-row__copy">
                      <span className="dash-stack-row__label">
                        <span className={`dash-stack-row__tone dash-stack-row__tone--${row.tone}`} />
                        {row.label}
                      </span>
                      <span className="dash-stack-row__meta">{row.meta}</span>
                    </div>
                    <div className="dash-stack-row__value">{row.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : null}

      <CallsBreakdownDialog
        open={breakdownFilter !== null}
        onClose={() => setBreakdownFilter(null)}
        filter={breakdownFilter}
        direction={directionFilter}
        calls={conversationsList}
        leads={leadsList}
        totals={displayStats}
        timeZone={practiceTimezone}
        fixedTimezone={fixedTimezone}
      />
    </div>
  );
};
